/**
 * V2 Personal categories — the data module (docs/JOURNAL-PLAN.md §11).
 *
 * A personal category is a shelf the user makes for their own yoinks:
 * "My restaurants", "Dive bars". A yoink filed under one is a REGULAR
 * yoink — real page, on the owner's profile and on place pages for every
 * viewer — it just doesn't ride the public discovery surfaces (feeds,
 * search, map pins) or count toward site-category collections. Nothing
 * here is private anymore; the old journal_entries layer is gone
 * (migration 20260811d folded its rows into yoinks).
 *
 * Store slice owned here: personalUnavailable (yoinks.personal_category_id
 * not applied yet → the form hides its personal shelves instead of
 * offering a save that cannot land — the listsUnavailable idiom).
 */
import * as gateway from '../../core/gateway.js';
import { cached, invalidate } from '../../core/query.js';
import { FEED_SELECT } from '../../ui/card.js';
import { slugify } from '../../../assets/js/shared/slugify.js';

/**
 * The four public discovery surfaces (feed, map, search, ticker) all read
 * yoinks through here so "personal yoinks stay off discovery" is one
 * filter in one place. Pre-migration the column 400s the whole query —
 * remember that once and fall back to unfiltered, so a merge that beats
 * the SQL degrades to "personal yoinks briefly visible in feeds"
 * (cosmetic — they aren't secret) instead of a dead Discover page.
 */
let personalColumnMissing = false;
export async function queryPublicYoinks(params) {
  if (!personalColumnMissing) {
    const result = await gateway.rawQuery(gateway.TABLES.YOINKS, {
      ...params,
      filters: { ...(params.filters || {}), personal_category_id: 'is.null' },
    });
    if (!result.error || !/HTTP 400/.test(result.error.message)) return result;
    personalColumnMissing = true;
  }
  return gateway.rawQuery(gateway.TABLES.YOINKS, params);
}

/**
 * Probe once per session whether the 20260811d migration is applied.
 * Failure hides the form's personal shelves rather than erroring.
 */
let probing = false;
export async function loadPersonalAvailability(store) {
  const state = store.get();
  if (!state.user || probing || typeof state.personalUnavailable === 'boolean') return;
  probing = true;
  try {
    const { error } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
      select: 'id,personal_category_id',
      filters: { user_id: `eq.${state.user.id}` },
      limit: 1,
    });
    if (error) throw error;
    store.set({ personalUnavailable: false });
  } catch {
    store.set({ personalUnavailable: true });
  } finally {
    probing = false;
  }
}

/**
 * One owner's shelves, oldest first (creation order — the tiles keep
 * their places). Readable by anyone since 20260811d (shelf names ride
 * yoink cards and profiles now); [] on any failure.
 */
export async function fetchPersonalCategoriesByOwner(ownerId) {
  if (!ownerId) return [];
  try {
    return await cached(`personal:shelves:${ownerId}`, async () => {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.PERSONAL_CATEGORIES, {
        select: '*',
        filters: { owner_id: `eq.${ownerId}` },
        order: 'created_at.asc',
        limit: 200,
      });
      if (error) throw error;
      return data || [];
    });
  } catch {
    return [];
  }
}

/**
 * Name the shelf on every personal row in a card list (§11): sets
 * `row.personalShelf` — the property yoink-view.js already renders — from
 * the owner's flat shelves read (cached per owner), never an FK embed.
 * One read per distinct owner; a row whose shelf can't be resolved is
 * left alone, so its card simply omits the row. Mutates and returns the
 * same array (idempotent — cached row objects keep the name).
 */
export async function attachPersonalShelves(rows) {
  const ownerOf = (r) => r.user_id || r.public_profiles?.id;
  const personal = (rows || []).filter((r) => r?.personal_category_id && !r.personalShelf && ownerOf(r));
  if (!personal.length) return rows;
  const owners = [...new Set(personal.map(ownerOf))];
  const shelvesByOwner = new Map(await Promise.all(
    owners.map(async (id) => [id, await fetchPersonalCategoriesByOwner(id)]),
  ));
  for (const row of personal) {
    const shelf = (shelvesByOwner.get(ownerOf(row)) || []).find((s) => s.id === row.personal_category_id);
    if (shelf) row.personalShelf = shelf;
  }
  return rows;
}

/** The signed-in user's own shelves — the log form's picker source. */
export async function fetchMyPersonalCategories(store) {
  return fetchPersonalCategoriesByOwner(store.get().user?.id);
}

/**
 * Create a personal category for an owner. Slug from the name with the
 * per-owner collision retry (two users can both own "my-restaurants";
 * one user can't) — the same loop as createList. RLS gates who may
 * create for whom: yourself, or anyone if you're an admin (20260811f —
 * the admin files a user's yoink onto a brand-new shelf of theirs).
 */
export async function createPersonalCategoryFor(ownerId, name) {
  if (!ownerId) return null;
  const base = slugify(name) || 'category';
  for (let attempt = 0; attempt < 5; attempt++) {
    const slug = attempt === 0 ? base : `${base}-${attempt + 1}`;
    const { data, error } = await gateway.rawInsert(gateway.TABLES.PERSONAL_CATEGORIES, {
      body: { owner_id: ownerId, name, slug },
    });
    if (!error) {
      invalidate('personal:shelves:');
      return data?.[0] || null;
    }
    if (!/HTTP 409/.test(error.message)) throw error;
  }
  throw new Error('That category name is taken — try another.');
}

/** The signed-in user's own create — the log form's usual path. */
export async function createPersonalCategory(store, name) {
  return createPersonalCategoryFor(store.get().user?.id, name);
}

/** Rename / re-emoji / re-story a personal category (owner via RLS). */
export async function updatePersonalCategory(categoryId, patch) {
  const { data, error } = await gateway.rawUpdate(gateway.TABLES.PERSONAL_CATEGORIES, {
    filters: { id: `eq.${categoryId}` },
    body: patch,
  });
  if (error) throw error;
  invalidate('personal:');
  return data?.[0] || null;
}

/**
 * Delete a personal category (owner or admin via RLS). Its yoinks are
 * untouched — the FK is on delete set null, so they unfile into the
 * owner's Uncategorized tile, never disappear.
 */
export async function deletePersonalCategory(categoryId) {
  const { error } = await gateway.rawDelete(gateway.TABLES.PERSONAL_CATEGORIES, {
    filters: { id: `eq.${categoryId}` },
  });
  if (error) throw error;
  invalidate('personal:');
  invalidate('profile-all-yoinks:'); // their shelf pointer just went null
}

/**
 * A personal category's page (/user/{u}/category/{slug}): owner profile,
 * the shelf, and its yoinks newest first — the same FEED_SELECT rows the
 * profile grid renders, so the cards are ordinary yoink cards.
 */
export async function fetchPersonalCategoryPage(username, slug) {
  return cached(`personal:page:${username.toLowerCase()}:${slug}`, async () => {
    const { data: profiles, error: profileError } = await gateway.rawQuery(gateway.TABLES.PUBLIC_PROFILES, {
      select: 'id,username,full_name,avatar_url',
      filters: { username: `ilike.${username}` },
      limit: 1,
    });
    if (profileError) throw profileError;
    const owner = profiles?.[0];
    if (!owner) return null;
    const { data: shelves, error: shelfError } = await gateway.rawQuery(gateway.TABLES.PERSONAL_CATEGORIES, {
      select: '*',
      filters: { owner_id: `eq.${owner.id}`, slug: `eq.${slug}` },
      limit: 1,
    });
    if (shelfError) throw shelfError;
    const shelf = shelves?.[0];
    if (!shelf) return null;
    const { data: yoinks, error: yoinksError } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
      select: FEED_SELECT,
      filters: { personal_category_id: `eq.${shelf.id}` },
      order: 'created_at.desc,id.desc',
      limit: 1000,
    });
    if (yoinksError) throw yoinksError;
    // Every row here is on this shelf — name it without a second read
    for (const y of yoinks || []) y.personalShelf = shelf;
    return { owner, shelf, yoinks: yoinks || [] };
  }, { ttl: 15_000 });
}
